import { BiSolidBookOpen, BiSolidWrench } from "react-icons/bi";
import { ReactComponent as BrandEquityIcon } from "./Icons/BrandEquity.svg";
import { ReactComponent as BrandHealthIcon } from "./Icons/Brand Health Icon.svg";
import { ReactComponent as CommerceIcon } from "./Icons/Commerce Icon.svg";
import { ReactComponent as CompetitiveIcon } from "./Icons/Competitive Icon.svg";
import { ReactComponent as CrossChannelIcon } from "./Icons/Cross Channel Icon.svg";
import { ReactComponent as DisplayIcon } from "./Icons/Display Icon.svg";
import { ReactComponent as LinearIcon } from "./Icons/Linear TV Icon.svg";
import { ReactComponent as MMMIcon } from "./Icons/MMM_Icon.svg";
import { ReactComponent as SocialIcon } from "./Icons/Social Icon.svg";
import { ReactComponent as StreamingIcon } from "./Icons/Streaming TV Icon.svg";
import { ReactComponent as TVADCrossChannelIcon } from "./Icons/TVAD Cross Channel Icon.svg";
import { ReactComponent as YouTubeIcon } from "./Icons/YouTube Icon.svg";
import { TbShoppingCartSearch } from "react-icons/tb";
import * as R from "ramda";
import {
  MdHeadphones,
  MdOutlinePalette,
  MdInsertChart,
  MdPersonPinCircle,
  MdLightMode,
  MdBusinessCenter,
  MdEmail,
  MdAutoGraph,
  MdOutlineLocalShipping,
  MdRadio,
} from "react-icons/md";
import {
  COMMERCE,
  CROSS_CHANNEL,
  LINEAR,
  PROGRAMMATIC,
  SEARCHSHOPPING,
  SOCIAL,
  STREAMING_PLUS,
  YOUTUBE,
} from "@blisspointmedia/bpm-types/dist/MetricsTable";
import { EMAIL, RADIO, getBenchamrkingChannels } from "@blisspointmedia/bpm-types/dist/ScpChannels";
import { COMMERCE_OPS } from "@blisspointmedia/bpm-types/dist/CommerceOps";

export const ENABLED_BENCHMARKING_CHANNELS = getBenchamrkingChannels();

const createChannelSubPages = (channel, options = {}) => {
  const pageID =
    channel === "search-shopping"
      ? SEARCHSHOPPING
      : channel === "cross-channel"
      ? CROSS_CHANNEL
      : channel;
  const subPages = {
    Overview: {
      requiresClient: true,
      to: `${channel}/overview`,
      roleRequired: 10,
      testCompanyParams: {
        channel: pageID,
        pageID,
      },
    },
    "Metrics Table": {
      requiresClient: true,
      to: `${channel}/metrics-table`,
      roleRequired: 10,
      testCompanyParams: {
        channel: pageID,
        pageID,
      },
    },
    Delivery: {
      requiresClient: true,
      to: `${channel}/delivery`,
      roleRequired: 10,
      testCompanyParams: {
        channel: pageID,
        pageID,
      },
      ...(options.deliveryExperiment ? { experiment: options.deliveryExperiment } : {}),
    },
  };
  return subPages;
};

export const CHANNELS_MAP = {
  Audio: STREAMING_PLUS[1],
  Commerce: COMMERCE,
  "Commerce Ops": COMMERCE_OPS,
  Display: STREAMING_PLUS[2],
  "Linear TV": LINEAR,
  Programmatic: PROGRAMMATIC,
  Radio: RADIO,
  Social: SOCIAL,
  "Search & Shopping": SEARCHSHOPPING,
  "Streaming TV": STREAMING_PLUS[0],
  YouTube: YOUTUBE,
  Custom: "custom",
  Email: "email",
};
export const CHANNELS_LINKS = [
  "Cross-Channel",
  "TVAD Cross-Channel",
  "Audio",
  "Commerce",
  "Commerce Ops",
  "Display",
  "Linear TV",
  "Programmatic",
  "Radio",
  "Search & Shopping",
  "Social",
  "Streaming TV",
  "YouTube",
  "Custom",
  "Email",
];
export const CHANNEL_ICONS = {
  "Cross-Channel": <CrossChannelIcon />,
  "TVAD Cross-Channel": <TVADCrossChannelIcon />,
  Audio: <MdHeadphones />,
  Commerce: <CommerceIcon />,
  "Commerce Ops": <MdOutlineLocalShipping />,
  Display: <DisplayIcon />,
  "Linear TV": <LinearIcon />,
  Programmatic: <DisplayIcon />,
  Radio: <MdRadio />,
  "Search & Shopping": <TbShoppingCartSearch />,
  Social: <SocialIcon />,
  "Streaming TV": <StreamingIcon />,
  YouTube: <YouTubeIcon />,
  Custom: <MdInsertChart />,
  Email: <MdEmail />,
};

export const ALL_EXTERNAL_TOOLS_LINKS = [
  "Creatives",
  "Competitive",
  "Customer Insights",
  "Forecasting",
  "rMMM",
  "Brand Equity",
  "Brand Equity Amazon",
  "Geo MMM",
  "Brand Health",
  "Brand Health Google",
  "Pacing",
];
export const TOOLS_LINKS = [
  "Creatives",
  "Competitive",
  "Customer Insights",
  "Forecasting",
  "MMM",
  "rMMM",
  "Brand Equity",
  "Geo MMM",
  "Pacing",
];
export const TOOLS_ICONS = {
  Competitive: <CompetitiveIcon />,
  Creatives: <MdOutlinePalette />,
  "Customer Insights": <MdOutlinePalette />,
  Forecasting: <MdLightMode />,
  MMM: <MMMIcon />,
  BrandEquity: <BrandEquityIcon />,
  "Geo MMM": <MMMIcon />,
  Pacing: <MdAutoGraph />,
};

export const LOCATIONS = {
  // Channels
  "Cross-Channel": {
    subPages: {
      ...createChannelSubPages("cross-channel"),
      "Total Business": {
        requiresClient: true,
        to: "cross-channel/total-business",
        roleRequired: 10,
        testCompanyParams: {
          channel: CROSS_CHANNEL,
          pageID: CROSS_CHANNEL,
        },
      },
    },
  },
  "TVAD Cross-Channel": {
    to: "tvad-cross-channel",
    requiresClient: true,
    experiment: "showLegacyTVADPages",
  },
  Audio: {
    subPages: {
      ...createChannelSubPages(STREAMING_PLUS[1], { deliveryExperiment: "showLegacyTVADPages" }),
      Performance: {
        to: "audio/performance",
        requiresClient: true,
        roleRequired: 10,
        experiment: "showLegacyTVADPages",
      },
      "Geo Response": { requiresClient: true, to: "audio/geo-response", roleRequired: 10 },
    },
  },
  Display: {
    subPages: {
      ...createChannelSubPages(STREAMING_PLUS[2], { deliveryExperiment: "showLegacyTVADPages" }),
      Performance: {
        to: "display/performance",
        requiresClient: true,
        roleRequired: 10,
        experiment: "showLegacyTVADPages",
      },
    },
  },
  Email: {
    subPages: {
      ...createChannelSubPages(EMAIL),
    },
  },
  "Linear TV": {
    subPages: {
      ...createChannelSubPages(LINEAR, { deliveryExperiment: "showLegacyTVADPages" }),
      Performance: {
        to: "linear/performance",
        requiresClient: true,
        roleRequired: 10,
        experiment: "showLegacyTVADPages",
      },
      "Spike Chart": {
        to: "linear/spike",
        requiresClient: true,
        roleRequired: 10,
        testCompanyParams: {
          testCompanyPageKey: LINEAR,
        },
      },
    },
    "Client Planning Tools": {
      "Linear Optimizations": { to: "linear/optimizations", requiresClient: true, roleRequired: 1 },
      "Linear ML Diagnostics": { to: "linear/diagnostics", requiresClient: true, roleRequired: 1 },
      "Linear Buying": { to: "linear/buying", requiresClient: true, roleRequired: 1 },
      "Linear Orders": { to: "linear/orders", requiresClient: true, roleRequired: 1 },
      "Linear Plans": { to: "linear/plans", requiresClient: true, roleRequired: 1 },
    },
    "Channel Buying": {
      "Linear Assumptions": { to: "linear/assumptions", roleRequired: 1 },
      "Linear CPMS": { to: "linear/cpms", roleRequired: 1 },
      "Linear Global Constraints": { to: "linear/constraints", roleRequired: 1 },
      "Linear Pacing": { to: "pacing/linear", roleRequired: 1 },
    },
    "Channel Operations": {
      "Process Post Logs": { to: "process", roleRequired: 1 },
      "Linear Networks": { to: "linear/networks", roleRequired: 1 },
      "Linear Buying Assignments": { to: "linear/assignments", roleRequired: 1 },
      "Pre Logs Tracker": { to: "logs/pre", roleRequired: 1 },
      "Post Logs Tracker": { to: "logs/post", roleRequired: 1 },
      "Linear AdOps": { to: "adops/linear", roleRequired: 1 },
      "ICON Local Linear Mappings": { to: "linear/icon-local-mappings", roleRequired: 1 },
      "Credit Memos": { to: "credit-memos", roleRequired: 1 },
    },
  },
  "Search & Shopping": {
    subPages: {
      ...createChannelSubPages("search-shopping"), // SEARCHSHOPPING
    },
    "Client Planning Tools": {
      "AdCopy AI": {
        requiresClient: true,
        to: "adcopy-ai",
        roleRequired: 1,
      },
    },
  },
  Programmatic: {
    subPages: {
      ...createChannelSubPages("programmatic"), // PROGRAMMATIC
    },
  },
  Radio: {
    subPages: {
      ...createChannelSubPages(RADIO),
    },
    "Channel Buying": {
      "Radio Buying": {
        requiresClient: true,
        experiment: "enableRadioBuying",
        roleRequired: 1,
        to: "radio/buying",
      },
    },
  },
  "Streaming TV": {
    subPages: {
      ...createChannelSubPages(STREAMING_PLUS[0], { deliveryExperiment: "showLegacyTVADPages" }),
      Performance: {
        to: "streaming/performance",
        requiresClient: true,
        roleRequired: 10,
        experiment: "showLegacyTVADPages",
      },
      "Geo Response": { requiresClient: true, to: "streaming/geo-response", roleRequired: 10 },
    },
    "Client Planning Tools": {
      "Creative Allocations": { to: "streaming/creatives", requiresClient: true, roleRequired: 1 },
      "Geo Targeting": {
        experiment: "enableStreamingGeoTargeting",
        requiresClient: true,
        roleRequired: 1,
        to: "streaming/geo-targeting",
      },
      "Streaming Buying": { to: "streaming/buying", requiresClient: true, roleRequired: 1 },
      "Streaming ML Diagnostics": {
        to: "streaming/diagnostics",
        requiresClient: true,
        roleRequired: 1,
      },
      "Streaming Optimizations": {
        to: "streaming/optimizations",
        requiresClient: true,
        roleRequired: 1,
      },
      "Flight Plan": { to: "streaming/plan", requiresClient: true, roleRequired: 1 },
    },
    "Channel Buying": {
      "Active Streaming Media (formerly Active OTT)": { to: "activeott", roleRequired: 1 },
      "Display Pacing": {
        experiment: "enableDisplayPacing",
        requiresClient: false,
        to: "pacing/display",
        roleRequired: 1,
      },
      "Order Status": { to: "streaming/status", requiresClient: true, roleRequired: 1 },
      "Streaming Global Constraints": { to: "streaming/constraints", roleRequired: 1 },
      "Streaming Pacing": { to: "pacing/streaming", requiresClient: false, roleRequired: 1 },
      Reports: { to: "streaming/reports", requiresClient: true, roleRequired: 1 },
      Wizard: { to: "streaming/wizard", requiresClient: true, roleRequired: 1 },
    },
    "Channel Operations": {
      "Streaming AdOps": { to: "adops/streaming", roleRequired: 1 },
      "Streaming Discrepancies": { to: "pacing/discrepancies", roleRequired: 1 },
      "Streaming Networks": { to: "streaming/networks", roleRequired: 1 },
    },
  },
  YouTube: {
    subPages: {
      ...createChannelSubPages(YOUTUBE),
    },
    "Client Planning Tools": {
      Planning: {
        requiresClient: true,
        roleRequired: 1,
        to: "youtube/planning",
      },
    },
  },
  Commerce: {
    subPages: {
      ...createChannelSubPages(COMMERCE),
      "AMC Insights": { requiresClient: true, to: "amc-insights", roleRequired: 10 },
    },
    "Channel Operations": {
      "Commerce Tools": { requiresClient: true, to: "commerce-tools", roleRequired: 1 },
      "Commerce Scraper": { requiresClient: false, to: "commerce-scraper", roleRequired: 1 },
    },
  },
  "Commerce Ops": {
    subPages: {
      Overview: { to: "commerce-ops/overview", requiresClient: true, roleRequired: 10 },
      Inventory: { to: "commerce-ops/inventory", requiresClient: true, roleRequired: 10 },
      "Keywords/Traffic": {
        to: "commerce-ops/keywords-and-traffic",
        requiresClient: true,
        roleRequired: 10,
      },
      Buybox: { to: "commerce-ops/buybox", requiresClient: true, roleRequired: 10 },
    },
  },
  Social: {
    subPages: {
      ...createChannelSubPages(SOCIAL),
    },
    "Client Planning Tools": {
      "Meta Buying": {
        to: "social/meta-buying",
        requiresClient: true,
        roleRequired: 2,
        experiment: "enableMetaBuying",
      },
      "Meta Signal Dashboard": {
        to: "social/meta-signal-dashboard",
        requiresClient: true,
        roleRequired: 2,
        experiment: "enableMetaSignalDashboard",
      },
      "Meta Lift Study": {
        to: "meta-lift-study/create",
        requiresClient: true,
        roleRequired: 10,
      },
    },
  },
  Custom: { to: "custom", requiresClient: true, roleRequired: 10 },
  Creatives: {
    "Creative Insights Internal": {
      experiment: "creativeInsightsInternalCompanies",
      requiresClient: true,
      to: "creative-insights",
      roleRequired: 2,
      mediaTypesRequired: [...STREAMING_PLUS, LINEAR],
    },
    "Creative Insights": {
      experiment: "creativeInsightsExternalCompanies",
      requiresClient: true,
      to: "creative-insights",
      roleRequired: 10,
      mediaTypesRequired: [...STREAMING_PLUS, LINEAR],
    },
    Creatives: {
      to: "creatives",
      requiresClient: true,
      roleRequired: 10,
      mediaTypesRequired: [...STREAMING_PLUS, LINEAR],
    },
  },
  Competitive: {
    "Competitive Metrics": {
      requiresClient: true,
      to: "competitive/metrics",
      roleRequired: 10,
    },
    "Spot Details": {
      requiresClient: true,
      to: "competitive/spots",
      roleRequired: 10,
    },
  },
  "Customer Insights": {
    to: "customer-insights",
    requiresClient: true,
    icon: <MdPersonPinCircle />,
    experiment: "enableCustomerInsights",
  },
  Forecasting: {
    icon: <MdLightMode />,
    requiresClient: true,
    to: "forecasting",
    experiment: "enableForecasting",
  },
  rMMM: {
    experiment: "enableMMMNavbarGui",
    icon: <MMMIcon />,
    requiresClient: true,
    to: "rmmm",
    roleRequired: 10,
  },
  "Geo MMM": {
    experiment: "enableGeoMMM",
    icon: <MMMIcon />,
    requiresClient: true,
    to: "geo-mmm",
    roleRequired: 10,
  },
  "Brand Equity": {
    experiment: "enableBrandEquityNavbar",
    icon: <BrandEquityIcon />,
    requiresClient: true,
    to: "brand-equity",
    roleRequired: 10,
  },
  "Brand Health": {
    experiment: "enableBrandHealthMetrics",
    icon: <BrandHealthIcon />,
    requiresClient: true,
    to: "brand-health-metrics",
  },
  "Brand Health Google": {
    icon: <BrandHealthIcon />,
    requiresClient: true,
    to: "brand-health-metrics/google",
  },
  Pacing: { requiresClient: true, to: "pacing", roleRequired: 10 },
  "Internal-only": {
    "Client-Specific Tools": { icon: <MdBusinessCenter /> },
    "General Tools": { icon: <BiSolidWrench /> },
    "Incrementality Playbook": { icon: <BiSolidBookOpen />, to: "incrementality-playbook" },
  },
  // Internal Tools for all channels
  "Client-Specific Tools": {
    "Client Reporting": {
      "Campaign Audit": {
        experiment: "enableCampaignAudit",
        to: "campaign-audit",
        requiresClient: true,
        roleRequired: 1,
      },
      Benchmarking: {
        to: "cross-client-benchmarking",
        requiresClient: true,
        roleRequired: 1,
        mediaTypesRequired: ENABLED_BENCHMARKING_CHANNELS.map(chan => chan.name),
      },
      "Data Freshness": { to: "data-freshness", requiresClient: true, roleRequired: 1 },
      "Expected Bookings": { to: "bookings", requiresClient: true, roleRequired: 1 },
      "Mobius Slides (Legacy)": {
        experiment: "enableMobiusSlidesCompany",
        requiresClient: true,
        to: "mobius-slides",
        roleRequired: 1,
      },
      Pacing: { to: "pacing", requiresClient: true, roleRequired: 1 },
      Reporting: { to: "reporting", requiresClient: true, roleRequired: 1 },
      "TVAD Slides (Legacy)": { to: "slides", requiresClient: true, roleRequired: 1 },
      "Slide Generator (new 2026)": {
        to: "slide-generator",
        requiresClient: true,
        roleRequired: 1,
      },
    },
    Operations: {
      "Automated Alerting": { to: "automated-alerting", requiresClient: true, roleRequired: 1 },
      "Goal Intake Tool": {
        to: "goal-intake-tool",
        requiresClient: true,
        roleRequired: 1,
      },
      "Edit Client": { to: "edit-client", requiresClient: true, roleRequired: 1 },
    },
    CRO: {
      "CRO Onboard": { to: "cro-onboard", requiresClient: true, roleRequired: 2 },
      "Web Traffic Optimization": { to: "wto", requiresClient: true, roleRequired: 2 },
    },
    Dev: {
      "SOT/GA4 Mapping": {
        roleRequired: 2,
        to: "sot-ga4-mapping-tool",
        requiresClient: true,
      },
      "Segmentation Mapping": {
        roleRequired: 2,
        to: "segmentation-mapping",
        requiresClient: true,
      },
      Properties: { to: "properties", requiresClient: true, roleRequired: 1 },
      "KPI Mapping": { to: "kpi-mapping", roleRequired: 1, requiresClient: true },
    },
  },
  "General Tools": {
    "Client Reporting": {
      "Query Exporter": { to: "query-exporter", roleRequired: 1 },
    },
    Operations: {
      "Client Profitability Survey": { to: "client-profitability-survey", roleRequired: 1 },
      "Account Planning": { to: "account-planning", roleRequired: 1 },
      "Add New Client": { to: "add-new-client", roleRequired: 0 },
      "User Admin": { to: "user-admin", roleRequired: 1 },
      Invoicing: { to: "invoicing", roleRequired: 1 },
      "CDN Uploader": { to: "image-uploader", roleRequired: 1 },
    },
    CRO: {
      "Site Monitoring": { to: "site-monitoring", roleRequired: 2 },
    },
    "Feed Management": {
      "Feed Management": { to: "feed-management", roleRequired: 2 },
    },
    Media: {
      "Device Breakout": { to: "device-breakout", roleRequired: 1 },
      Admin: { to: "admin", roleRequired: 1 },
      Calendar: { to: "calendar", roleRequired: 1 },
    },
    Dev: {
      "Batch Failures": { to: "batch-failures", roleRequired: 1 },
      "System Health": { to: "system-health", roleRequired: 1 },
      "Dag Status": { to: "dag-status", roleRequired: 1 },
      "KPI Status": { to: "kpi-status", roleRequired: 1 },
      Companies: { to: "companies", roleRequired: 1 },
      "Feature Experiments": {
        to: "feature-experiments",
        roleRequired: 1,
      },
    },
  },
  "Reference Docs": {
    to: "/reference",
  },
  "Log Out": {
    to: "logout",
  },
};

export const flattenRoutes = obj => {
  const flat = {};
  for (const i in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, i)) {
      if (
        obj[i] &&
        (!R.isNil(obj[i].to) || !R.isNil(obj[i].onClick) || !R.isNil(obj[i].requiresClient))
      ) {
        flat[i] = obj[i];
      } else if (typeof obj[i] === "object") {
        const flatObj = flattenRoutes(obj[i]);
        for (const x in flatObj) {
          if (Object.prototype.hasOwnProperty.call(flatObj, x)) {
            flat[`${i}/${x}`] = flatObj[x];
          }
        }
      }
    }
  }
  return flat;
};

const removeDisabledRoutesRecursive = ({
  experiments,
  locations,
  mediaTypes,
  role,
  pageAccessSettings,
  isInternal,
}) => {
  const keys = R.keys(locations);
  if (
    experiments.includes("enablePrincipalBasedBuying") &&
    !isInternal &&
    [
      "streaming/performance",
      "streaming/delivery",
      "streaming/metrics-table",
      "audio/performance",
      "audio/delivery",
      "audio/metrics-table",
      "display/performance",
      "display/delivery",
      "display/metrics-table",
    ].includes(locations.to)
  ) {
    return null;
  } else if (
    keys.includes("to") ||
    keys.includes("onClick") ||
    keys.includes("experiment") ||
    keys.includes("requiresClient") ||
    keys.includes("icon") ||
    keys.includes("roleRequired")
  ) {
    if (!R.isNil(locations.experiment) && !experiments.includes(locations.experiment)) {
      return null;
    } else if (!R.isNil(locations.roleRequired) && locations.roleRequired < role) {
      return null;
    } else if (!R.isNil(locations.mediaTypesRequired)) {
      let remove = true;
      for (const mediaTypeRequired of locations.mediaTypesRequired) {
        if (R.map(mt => (mt === "tv" ? LINEAR : mt), mediaTypes).includes(mediaTypeRequired)) {
          remove = false;
          break;
        }
      }
      if (remove) {
        return null;
      }
    }

    return locations;
  } else {
    for (const key of keys) {
      locations[key] = removeDisabledRoutesRecursive({
        experiments,
        locations: locations[key],
        mediaTypes,
        role,
        isInternal,
      });
      if (R.isNil(locations[key])) {
        delete locations[key];
      }
    }
  }

  if (!R.isEmpty(pageAccessSettings) && !isInternal) {
    removeSingleChannelTVADSubPages(locations, pageAccessSettings);
  }
  return locations;
};

// For clients that have the new TVAD single channel pages set to internal only, remove them from the navbar for external users.
// We will only display the legacy performance pages for these clients.
const removeSingleChannelTVADSubPages = (locations, pageAccessSettings) => {
  const PAGE_ACCESS_SETTINGS_MAP = {
    streaming_single_channel: "Streaming TV",
    audio_single_channel: "Audio",
    display_single_channel: "Display",
    linear_single_channel: "Linear TV",
    youtube_single_channel: "YouTube",
  };
  const SUB_PAGES = ["Overview", "Metrics Table"];

  for (let pageName of R.keys(pageAccessSettings)) {
    if (
      R.keys(PAGE_ACCESS_SETTINGS_MAP).includes(pageName) &&
      pageAccessSettings[pageName] === "internal"
    ) {
      const prettyPageName = PAGE_ACCESS_SETTINGS_MAP[pageName];
      // Remove Overview, Metrics Table, and Delivery sub pages
      for (const subPage of SUB_PAGES) {
        if (locations[prettyPageName]?.subPages?.[subPage]) {
          delete locations[prettyPageName].subPages[subPage];
        }
      }
    }
  }
};

const removeEmptyLocations = locations => {
  const keys = R.keys(locations);
  if (
    keys.includes("to") ||
    keys.includes("onClick") ||
    keys.includes("experiment") ||
    keys.includes("requiresClient") ||
    keys.includes("icon") ||
    keys.includes("roleRequired")
  ) {
    return locations;
  }
  if (!keys.length) {
    return null;
  } else {
    for (const key of keys) {
      locations[key] = removeEmptyLocations(locations[key]);
      if (R.isNil(locations[key])) {
        delete locations[key];
      }
    }
  }
  return locations;
};

export const removeDisabledRoutes = ({
  experiments,
  role,
  mediaTypes,
  pageAccessSettings,
  isInternal,
}) => {
  const locationsCopy = R.clone(LOCATIONS);
  let locationsFiltered = removeDisabledRoutesRecursive({
    experiments,
    locations: locationsCopy,
    mediaTypes,
    role,
    pageAccessSettings,
    isInternal,
  });
  const creativeKeys = R.keys(locationsFiltered.Creatives);
  if (creativeKeys.length === 1) {
    locationsFiltered.Creatives = locationsFiltered.Creatives[creativeKeys[0]];
  }
  const competitiveKeys = R.keys(locationsFiltered.Competitive);
  if (competitiveKeys.length === 1) {
    locationsFiltered.Competitive = locationsFiltered.Competitive[competitiveKeys[0]];
  }
  locationsFiltered = removeEmptyLocations(locationsFiltered);
  const internalToolsKeys = [
    R.keys(locationsFiltered["Client-Specific Tools"]),
    ...R.keys(locationsFiltered["General Tools"]),
  ];
  if (!internalToolsKeys.length) {
    delete locationsFiltered["Internal Only"];
  }
  return locationsFiltered;
};

export const getChannelTools = (LOCATIONS, channel, category) => {
  const clientTools = [];
  if (!R.keys(LOCATIONS).includes(channel)) {
    return clientTools;
  }
  const toolsMap = R.defaultTo(
    {},
    LOCATIONS[["Audio", "Display"].includes(channel) ? "Streaming TV" : channel][category]
  );
  if (category === "Channel Buying" && channel === "Display") {
    toolsMap["Display Pacing"] = {
      experiment: "enableDisplayPacing",
      requiresClient: false,
      to: "pacing/display",
    };
    delete toolsMap["Streaming Pacing"];
  } else if (category === "Channel Buying" && ["Audio", "Streaming TV"].includes(channel)) {
    toolsMap["Streaming Pacing"] = { to: "pacing/streaming", requiresClient: false };
    delete toolsMap["Display Pacing"];
  }
  R.keys(toolsMap).forEach(key => clientTools.push({ ...toolsMap[key], title: key }));
  return clientTools.sort((a, b) => a.title.localeCompare(b.title));
};

export const getInternalTools = (LOCATIONS, category, clientSpecific) => {
  const internalTools = [];
  const internalToolsMap = R.defaultTo(
    {},
    LOCATIONS[clientSpecific ? "Client-Specific Tools" : "General Tools"][category]
  );
  R.keys(internalToolsMap).forEach(key =>
    internalTools.push({ ...internalToolsMap[key], title: key })
  );
  return R.uniq(internalTools).sort((a, b) => a.title.localeCompare(b.title));
};

export const CLIENT_TOOLS_HEADERS = [
  "Client Planning Tools",
  "Channel Buying",
  "Channel Operations",
];
export const INTERNAL_TOOLS_HEADERS = [
  "Client Reporting",
  "Operations",
  "Media",
  "Dev",
  "CRO",
  "Feed Management",
];

const currentNavBarItemHelper = (object, to, keys) => {
  if (object && to.startsWith(object.to)) {
    return keys;
  }
  if (typeof object === "object") {
    for (let i = 0; i < R.keys(object).length; i++) {
      if (typeof object[R.keys(object)[i]] === "object") {
        const o = currentNavBarItemHelper(object[R.keys(object)[i]], to, [
          ...keys,
          R.keys(object)[i],
        ]);
        if (o != null) {
          return o;
        }
      }
    }
  }
  return null;
};

export const currentNavBarItem = (object, to) => {
  const keys = currentNavBarItemHelper(object, to, []);
  if (keys && keys.length) {
    return keys;
  }
  return null;
};
